import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { contactsKeys } from './useContacts';

/**
 * Portal access status returned by the API.
 */
export interface PortalStatus {
  hasPortalAccess: boolean;
  portalUserId: string | null;
  portalUserStatus: 'invited' | 'active' | 'suspended' | null;
  portalEmail: string | null;
  invitationStatus: string | null;
}

/**
 * Query key for portal status.
 */
export const portalStatusKey = (contactId: string) =>
  [...contactsKeys.detail(contactId), 'portal-status'] as const;

/**
 * Fetches the portal access status for a contact.
 */
export function usePortalStatus(contactId: string) {
  return useQuery({
    queryKey: portalStatusKey(contactId),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: PortalStatus }>(
        `/contacts/${contactId}/portal-status`,
      );
      return response.data.data;
    },
    enabled: !!contactId,
  });
}

/**
 * Mutation to assign portal access to a contact.
 * Creates a portal user and sends an invitation email.
 */
export function useAssignPortal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contactId: string) => {
      const response = await apiClient.post<{
        success: boolean;
        data: { portalUserId: string; email: string; status: string };
        message: string;
      }>(`/contacts/${contactId}/assign-portal`);
      return response.data;
    },
    onSuccess: (_data, contactId) => {
      queryClient.invalidateQueries({ queryKey: contactsKeys.detail(contactId) });
      queryClient.invalidateQueries({ queryKey: portalStatusKey(contactId) });
    },
  });
}

/**
 * Mutation to cancel portal access for a contact.
 * Suspends the portal user account.
 */
export function useCancelPortal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contactId: string) => {
      const response = await apiClient.post<{ success: boolean; message: string }>(
        `/contacts/${contactId}/cancel-portal`,
      );
      return response.data;
    },
    onSuccess: (_data, contactId) => {
      queryClient.invalidateQueries({ queryKey: contactsKeys.detail(contactId) });
      queryClient.invalidateQueries({ queryKey: portalStatusKey(contactId) });
    },
  });
}
